import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControl, TextField } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";

import { AjaxResult } from "../../../enums/ajaxResult";
import { TimeZone } from "../../../enums/timeZone";
import { FormSection } from "../../shared/formSection";
import { FormField } from "../../shared/formField";
import { useApi } from "../../useApi";
import { ValidationErrors } from "../../shared/validationErrors";

import { DEFAULT_SITE_CONFIGURATION } from "./consts";
import { TimeZoneSelect } from "./TimeZoneSelect";

const missingGuidError = "Missing Service GUID";
const missingTokenError = "Missing Commissioning token";
const missingOrgIdError = "Missing Organization ID";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSiteAdded: () => void;
    serviceGuidFromParams: string | undefined;
    commissioningTokenFromQuery: string | undefined;
    orgId: number | undefined
}

export function AddSiteDialog({
    isOpen,
    onClose,
    onSiteAdded,
    serviceGuidFromParams,
    commissioningTokenFromQuery,
    orgId,
    children,
}: React.PropsWithChildren<Props>) {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [siteName, setSiteName] = useState("");
    const [timeZone, setTimeZone] = useState<TimeZone>(TimeZone.UTC);
    const [isActive, setIsActive] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);

    const handleSiteNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSiteName(event.currentTarget.value)
    }, []);

    function saveNewSite() {
        setErrors([]);
        if (!serviceGuidFromParams) {
            setErrors([missingGuidError]);
            return;
        }
        if (!commissioningTokenFromQuery) {
            setErrors([missingTokenError]);
            return;
        }
        if (!orgId) {
            setErrors([missingOrgIdError]);
            return;
        }
        setLoading(true);
        siteApi
            .post({
                organizationId: orgId,
                name: siteName,
                emailAddresses: "",
                serviceGuid: serviceGuidFromParams,
                commissioningToken: commissioningTokenFromQuery,
                excludeFromBilling: false,
                emailAlertsEnabled: true,
                referenceSiteId: "",
                timeZone: timeZone,
                maintenanceWindow: null,
                siteConfiguration: DEFAULT_SITE_CONFIGURATION,
                notes: null,
                isActive: isActive,
            })
            .then((r) => {
                if (r.result === AjaxResult.Success) {
                    onSiteAdded();
                    setSiteName("");
                    onClose();
                    enqueueSnackbar("Site created");
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to create site");
                }
                setLoading(false);
            });
    }

    return (
        <Dialog open={isOpen} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText="Create New Site">
                    {children}

                    <FormField label="Site Name" required={true}>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={siteName}
                                onChange={handleSiteNameChange}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>

                    <FormField label="Service Id" required={true}>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={serviceGuidFromParams}
                                disabled={true}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>

                    <FormField label="Time zone" required={true}>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TimeZoneSelect
                                value={timeZone}
                                onChange={setTimeZone}
                            />
                        </FormControl>
                    </FormField>

                    <FormField label="Deactivated">
                        <Checkbox
                            checked={!isActive}
                            color="primary"
                            onChange={(event) => {
                                setIsActive(!event.target.checked);
                            }}
                        />
                    </FormField>

                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>

            <DialogActions>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={saveNewSite}
                    disabled={loading || !orgId || !siteName}
                >
                    {loading ? "Saving..." : "Save"}
                </Button>

                <Button
                    color="secondary"
                    variant="contained"
                    onClick={onClose}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
